import { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { tolerantId } from "../utils/firestoreHelpers";

const ZEROS = {
  unreadChat: 0,
  pendingVerification: 0,
  activeIncidents: 0,
  activeMultas: 0,
  whatsappNeedsHuman: 0,
  pendingConsultas: 0,
  proximaLlamada: null,
};

/**
 * Contadores en tiempo real de pendientes del admin para el edificio activo:
 * chat de empleados sin leer, pagos por verificar, incidencias abiertas, multas
 * activas y conversaciones de WhatsApp/Habi escaladas a humano.
 *
 * Fuente ÚNICA de los badges — la consumen SidebarAdmin y el acceso rápido del
 * inicio (HomeAdmin) para que los números siempre coincidan.
 */
export function useAdminBadges(buildingid) {
  const [badges, setBadges] = useState(ZEROS);

  useEffect(() => {
    if (!buildingid) {
      setBadges(ZEROS);
      return undefined;
    }

    const buildingIdVariants = tolerantId(buildingid);
    const set = (key) => (value) => setBadges((b) => ({ ...b, [key]: value }));

    // Chat de empleados no leído
    const unsubChat = onSnapshot(
      query(collection(db, "employee_chats"), where("buildingid", "in", buildingIdVariants)),
      (snap) => {
        let total = 0;
        snap.forEach((d) => { total += d.data().unreadCountAdmin || 0; });
        set("unreadChat")(total);
      },
      (err) => console.error("useAdminBadges chat listener error:", err)
    );

    // Pagos pendientes de verificación
    const unsubPay = onSnapshot(
      query(
        collection(db, "payments"),
        where("buildingid", "in", buildingIdVariants),
        where("status", "==", "pendiente_verificacion")
      ),
      (snap) => set("pendingVerification")(snap.size),
      (err) => console.error("useAdminBadges payments listener error:", err)
    );

    // Incidencias abiertas
    const unsubInc = onSnapshot(
      query(
        collection(db, "incidents"),
        where("buildingid", "in", buildingIdVariants),
        where("status", "==", "abierta")
      ),
      (snap) => set("activeIncidents")(snap.size),
      (err) => console.error("useAdminBadges incidents listener error:", err)
    );

    // Multas activas
    const unsubMultas = onSnapshot(
      query(
        collection(db, "multas"),
        where("buildingid", "in", buildingIdVariants),
        where("status", "==", "activa")
      ),
      (snap) => set("activeMultas")(snap.size),
      (err) => console.error("useAdminBadges multas listener error:", err)
    );

    // WhatsApp/Habi: conversaciones que escalaron y necesitan atención humana.
    const unsubWa = onSnapshot(
      query(
        collection(db, "whatsapp_threads"),
        where("buildingId", "in", buildingIdVariants),
        where("needsHuman", "==", true)
      ),
      (snap) => set("whatsappNeedsHuman")(snap.size),
      (err) => console.error("useAdminBadges whatsapp listener error:", err)
    );

    // Consultas de residentes (Habi): pendientes de responder + próxima llamada
    // agendada (para el aviso del header, visible sin abrir el sidebar).
    // Query por adminEmails (mi bandeja) + edificio ('in' número/string), como
    // ConsultasAdmin: las reglas 0D piden ser staff vigente de ese edificio.
    const myEmail = (auth.currentUser?.email || "").toLowerCase();
    const variantsStr = buildingIdVariants.map(String);
    const unsubConsultas = !myEmail ? (() => {}) : onSnapshot(
      query(
        collection(db, "habi_consultas"),
        where("adminEmails", "array-contains", myEmail),
        where("buildingid", "in", buildingIdVariants)
      ),
      (snap) => {
        let pendientes = 0;
        let proxima = null;
        const ahora = new Date();
        snap.forEach((d) => {
          const c = d.data();
          if (!variantsStr.includes(String(c.buildingid))) return;
          const status = c.status || "pendiente";
          if (status === "pendiente") pendientes += 1;
          if (c.type === "llamada" && status !== "resuelta" && c.call?.date && c.call?.time) {
            const cuando = new Date(`${c.call.date}T${c.call.time}:00`);
            if (!Number.isNaN(cuando.getTime()) && cuando >= ahora) {
              if (!proxima || cuando < proxima.cuando) {
                proxima = {
                  cuando,
                  date: c.call.date,
                  time: c.call.time,
                  nombre: c.residentName || "Residente",
                };
              }
            }
          }
        });
        setBadges((b) => ({ ...b, pendingConsultas: pendientes, proximaLlamada: proxima }));
      },
      (err) => console.error("useAdminBadges consultas listener error:", err)
    );

    return () => { unsubChat(); unsubPay(); unsubInc(); unsubMultas(); unsubWa(); unsubConsultas(); };
  }, [buildingid]);

  return badges;
}
